import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'
import * as echarts from 'echarts'
import { safeChartInit } from '../../../utils/echarts'
import styled from 'styled-components'
import { Loading } from '../../atoms/Loading'
import { Flex } from '../../atoms/Flex'
import { Box } from '../../atoms/Box'
import {
  MarketTimingChartProps,
  DEFAULT_MARKET_TIMING_CHART_LABELS,
} from './MarketTimingChart.types'

const ChartContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`

const ChartWrapper = styled.div<{ $height: number }>`
  width: 100%;
  height: ${({ $height }) => $height}px;
`

const ErrorContainer = styled(Flex)`
  height: 350px;
  color: ${({ theme }) => theme.colors.text.secondary};
`

const LoadingContainer = styled(Flex)`
  height: 350px;
`

const ChartsGrid = styled(Box)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  width: 100%;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }

  @media print {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`

export interface MarketTimingChartRef {
  exportChart: () => string | null
  exportAllCharts: () => (string | null)[]
}

export const MarketTimingChart = forwardRef<MarketTimingChartRef, MarketTimingChartProps>(
  (
    {
      publicationLagData = [],
      dailyActivityData = [],
      loading = false,
      error = null,
      height = 400,
      labels = {},
    },
    ref
  ) => {
    const mergedLabels = { ...DEFAULT_MARKET_TIMING_CHART_LABELS, ...labels }
    const lagChartRef = useRef<HTMLDivElement>(null)
    const heatmapChartRef = useRef<HTMLDivElement>(null)
    const lagChartInstance = useRef<echarts.ECharts | null>(null)
    const heatmapChartInstance = useRef<echarts.ECharts | null>(null)

    useImperativeHandle(ref, () => ({
      exportChart: () => {
        if (lagChartInstance.current) {
          return lagChartInstance.current.getDataURL({
            type: 'png',
            pixelRatio: 2,
            backgroundColor: '#fff',
          })
        }
        return null
      },
      exportAllCharts: () => [
        lagChartInstance.current?.getDataURL({
          type: 'png',
          pixelRatio: 2,
          backgroundColor: '#fff',
        }) ?? null,
        heatmapChartInstance.current?.getDataURL({
          type: 'png',
          pixelRatio: 2,
          backgroundColor: '#fff',
        }) ?? null,
      ],
    }))

    useEffect(() => {
      if (!loading && !error && publicationLagData.length > 0 && lagChartRef.current) {
        initializeLagChart()
      }

      return () => {
        lagChartInstance.current?.dispose()
      }
    }, [publicationLagData, loading, error])

    useEffect(() => {
      if (!loading && !error && dailyActivityData.length > 0 && heatmapChartRef.current) {
        initializeHeatmapChart()
      }

      return () => {
        heatmapChartInstance.current?.dispose()
      }
    }, [dailyActivityData, loading, error])

    useEffect(() => {
      const handleResize = () => {
        lagChartInstance.current?.resize()
        heatmapChartInstance.current?.resize()
      }

      window.addEventListener('resize', handleResize)
      return () => window.removeEventListener('resize', handleResize)
    }, [])

    const initializeLagChart = () => {
      if (!lagChartRef.current || !publicationLagData || publicationLagData.length === 0) return

      if (lagChartInstance.current) {
        lagChartInstance.current.dispose()
      }

      lagChartInstance.current = safeChartInit(lagChartRef.current)

      // Sort by lag days descending
      const sortedData = [...publicationLagData].sort((a, b) => b.avg_lag_days - a.avg_lag_days)

      const option: echarts.EChartsOption = {
        title: {
          text: mergedLabels.publicationLag,
          subtext: mergedLabels.publicationLagSubtext,
          left: 'center',
          top: 10,
          textStyle: {
            fontSize: 16,
            fontWeight: 600,
          },
          subtextStyle: {
            fontSize: 12,
          },
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow',
          },
          formatter: (params: any) => {
            const param = params[0]
            const dataItem = sortedData[param.dataIndex]
            return `
            <div style="padding: 8px;">
              <div style="font-weight: 600; margin-bottom: 8px;">${param.name}</div>
              <div>${mergedLabels.avgLagDays}: ${Math.round(dataItem.avg_lag_days)}</div>
              <div>${mergedLabels.transactions}: ${dataItem.transaction_count.toLocaleString()}</div>
            </div>
          `
          },
        },
        grid: {
          left: '5%',
          right: '10%',
          top: '22%',
          bottom: '5%',
          containLabel: true,
        },
        xAxis: {
          type: 'value',
          name: mergedLabels.avgLagDays,
          nameLocation: 'middle',
          nameGap: 30,
          nameTextStyle: {
            fontSize: 12,
            fontWeight: 600,
          },
        },
        yAxis: {
          type: 'category',
          data: sortedData.map((d) => d.transaction_type),
          axisLabel: {
            fontSize: 11,
          },
        },
        series: [
          {
            type: 'bar',
            data: sortedData.map((d) => d.avg_lag_days),
            itemStyle: {
              color: (params: any) => {
                // Color gradient based on lag (red = longer lag, green = shorter lag)
                const maxLag = Math.max(...sortedData.map((d) => d.avg_lag_days))
                const ratio = params.value / maxLag
                if (ratio > 0.7) return '#ee6666'
                if (ratio > 0.4) return '#fac858'
                return '#91cc75'
              },
            },
            label: {
              show: true,
              position: 'right',
              formatter: (params: any) => `${Math.round(params.value)} days`,
              fontSize: 10,
            },
          },
        ],
      }

      lagChartInstance.current.setOption(option)
    }

    const initializeHeatmapChart = () => {
      if (!heatmapChartRef.current || !dailyActivityData || dailyActivityData.length === 0) return

      if (heatmapChartInstance.current) {
        heatmapChartInstance.current.dispose()
      }

      heatmapChartInstance.current = safeChartInit(heatmapChartRef.current)

      // Transform data for calendar heatmap
      const calendarData = dailyActivityData.map((item) => [item.date, item.transaction_count])

      // Get date range
      const dates = dailyActivityData.map((d) => new Date(d.date))
      const minDate = new Date(Math.min(...dates.map((d) => d.getTime())))
      const maxDate = new Date(Math.max(...dates.map((d) => d.getTime())))

      const option: echarts.EChartsOption = {
        title: {
          text: mergedLabels.activityCalendar,
          subtext: mergedLabels.activityCalendarSubtext,
          left: 'center',
          top: 10,
          textStyle: {
            fontSize: 16,
            fontWeight: 600,
          },
          subtextStyle: {
            fontSize: 12,
          },
        },
        tooltip: {
          trigger: 'item',
          formatter: (params: any) => {
            return `
            <div style="padding: 8px;">
              <div style="font-weight: 600; margin-bottom: 4px;">${params.value[0]}</div>
              <div>${mergedLabels.transactions}: ${params.value[1].toLocaleString()}</div>
            </div>
          `
          },
        },
        visualMap: {
          min: 0,
          max: Math.max(...dailyActivityData.map((d) => d.transaction_count)),
          type: 'piecewise',
          orient: 'horizontal',
          left: 'center',
          bottom: 10,
          inRange: {
            color: ['#ebedf0', '#c6e48b', '#7bc96f', '#239a3b', '#196127'],
          },
          pieces: [
            {
              min: 0,
              max: 0,
              label: mergedLabels.noActivity,
              color: '#ebedf0',
            },
            { min: 1, max: 5, label: '1-5' },
            { min: 6, max: 10, label: '6-10' },
            { min: 11, max: 20, label: '11-20' },
            { min: 21, label: '21+' },
          ],
        },
        calendar: {
          top: 80,
          left: 50,
          right: 30,
          cellSize: ['auto', 13],
          range: [minDate.toISOString().split('T')[0], maxDate.toISOString().split('T')[0]],
          itemStyle: {
            borderWidth: 0.5,
            borderColor: '#fff',
          },
          yearLabel: { show: true },
          dayLabel: {
            firstDay: 1,
            nameMap: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
          },
          monthLabel: {
            nameMap: 'en',
          },
        },
        series: [
          {
            type: 'heatmap',
            coordinateSystem: 'calendar',
            data: calendarData,
          },
        ],
      }

      heatmapChartInstance.current.setOption(option)
    }

    if (loading) {
      return (
        <LoadingContainer justify="center" align="center">
          <Loading size="large" />
        </LoadingContainer>
      )
    }

    if (error) {
      return (
        <ErrorContainer justify="center" align="center">
          {error}
        </ErrorContainer>
      )
    }

    if (publicationLagData.length === 0 && dailyActivityData.length === 0) {
      return (
        <ErrorContainer justify="center" align="center">
          {mergedLabels.noData}
        </ErrorContainer>
      )
    }

    return (
      <ChartContainer>
        <ChartsGrid>
          {publicationLagData.length > 0 && <ChartWrapper $height={height} ref={lagChartRef} />}
          {dailyActivityData.length > 0 && <ChartWrapper $height={height} ref={heatmapChartRef} />}
        </ChartsGrid>
      </ChartContainer>
    )
  }
)

MarketTimingChart.displayName = 'MarketTimingChart'
